import { Suspense, lazy, useEffect, useEffectEvent, useState } from 'react'
import { createFileRoute, notFound } from '@tanstack/react-router'
import { useQuery, useSuspenseQuery } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import { z } from 'zod'
import { usePostHog } from '@posthog/react'
import {
  questionnaireDashboardQueryOptions,
  questionQueryOptions,
  questionResponseQueryOptions,
} from '#/api/questionnaires'
import type { Question } from '#/api/questionnaires'
import { versionSignature, summariseAiUpdates } from '#/lib/response-versions'
import { useApproveAllAiUpdates, useRejectAllAiUpdates } from '#/api/ai-updates'
import { guidanceQueryOptions } from '#/api/guidance'
import { useCan } from '#/user/me'
import { useIsMobile } from '#/hooks/use-mobile'
import { RoutePending } from '#/components/route-pending'
import { RouteError } from '#/components/route-error'
import { useQuestionPageState } from '#/components/questionnaire/question-page/question-page-state'
import { AiUpdatesBanner } from '#/components/questionnaire/ai-updates-banner'
import { ResponseReviewBanner } from '#/components/questionnaire/response-review-banner'
import { RejectReasonDialog } from '#/components/questionnaire/reject-reason-dialog'
import {
  isAccordionMatrix,
  matrixRowLabels,
  isMatrix,
} from '#/components/questionnaire/inputs/shared'
import { QuestionInput } from '#/components/questionnaire/inputs/question-input'
import { QuestionPageHeader } from '#/components/questionnaire/question-page/question-page-header'
import { QuestionPicker } from '#/components/questionnaire/question-picker'
import { ResponseStatusChip } from '#/components/questionnaire/response-status-chip'
import { DataOwnerChip } from '#/components/questionnaire/data-owner-chip'
import {
  DrawerToggle,
  QuestionPageLayout,
} from '#/components/questionnaire/question-page/question-page-layout'
import {
  CONTEXT_PANES_INLINE_MIN_WIDTH,
  isContextPaneShown,
  visibleContextPanes,
} from '#/components/questionnaire/question-page/context-panes'
import { getReadOnlyReason } from '#/components/questionnaire/read-only-reason'
import {
  openContextPanes,
  revealPane,
  visibleLayout,
} from '#/components/questionnaire/question-page/question-dock'
import { EditViewMenu } from '#/components/questionnaire/question-page/question-panes'
import { useQuestionNavigation } from '#/components/questionnaire/use-question-navigation'
import { flattenLeafQuestionSections } from '#/components/questionnaire/question-tree'

// The drawer is the route's heaviest subtree (guidance, draft runs,
// provenance). Code-split it so the question itself — header, status, input —
// paints without waiting on the drawer's module graph to load and instrument.
const QuestionDrawer = lazy(() =>
  import('#/components/questionnaire/question-page/question-drawer').then(
    (m) => ({
      default: m.QuestionDrawer,
    }),
  ),
)
const QuestionPanelBody = lazy(() =>
  import('#/components/questionnaire/question-page/question-drawer').then(
    (m) => ({
      default: m.QuestionPanelBody,
    }),
  ),
)

// `draft_run` carries the run UUID of an in-flight or recently-completed AI
// draft. Persisting it in the URL is what lets a refresh keep polling the
// same run. Stored as a free-form string — the backend uses 32-hex no-dash
// UUIDs which don't match Zod's `.uuid()` RFC regex.
const questionSearchSchema = z.object({
  draft_run: z.string().optional(),
})

export const Route = createFileRoute(
  '/_authed/accounts/$accountId/workspaces/$workspaceId/questions/$questionId',
)({
  validateSearch: questionSearchSchema,
  loader: async ({ context, params }) => {
    const questionnaireId = context.workspace.questionnaire_id
    if (!questionnaireId) throw notFound()
    const question: Question = await context.queryClient.ensureQueryData(
      questionQueryOptions(
        params.accountId,
        params.workspaceId,
        questionnaireId,
        params.questionId,
      ),
    )
    const tasks: Array<Promise<unknown>> = [
      context.queryClient.ensureQueryData(
        questionResponseQueryOptions(
          params.accountId,
          params.workspaceId,
          questionnaireId,
          params.questionId,
          question.kind,
        ),
      ),
      // Keyboard nav (next/prev question, jump-by-status) reads the same
      // sections + response statuses the dashboard does; warm them so deep
      // links to a question respond to the first keypress.
      context.queryClient.ensureQueryData(
        questionnaireDashboardQueryOptions(
          params.accountId,
          params.workspaceId,
          questionnaireId,
        ),
      ),
    ]
    await Promise.all(tasks)
    return { questionnaireId }
  },
  pendingComponent: RoutePending,
  errorComponent: RouteError,
  component: QuestionDetailView,
})

function QuestionDetailView() {
  const { t } = useTranslation()
  const { accountId, workspaceId, questionId } = Route.useParams()
  const { questionnaireId } = Route.useLoaderData()
  const { draft_run: draftRunId } = Route.useSearch()
  const posthog = usePostHog()
  // Drawer tab + open/closed state and the open panes persist across question
  // navigation (held at the workspace layout), so they stay put as the user
  // moves between questions.
  const {
    panel,
    setPanel,
    drawerOpen,
    setDrawerOpen,
    activeUpdateId,
    setActiveUpdateId,
    paneLayout,
    setPaneLayout,
  } = useQuestionPageState()
  // Wide screens lay the context panes out beside the question; narrow ones
  // fall back to the single tabbed drawer.
  const wide = !useIsMobile(CONTEXT_PANES_INLINE_MIN_WIDTH)

  const { data: question } = useSuspenseQuery(
    questionQueryOptions(accountId, workspaceId, questionnaireId, questionId),
  )
  const { data: response } = useSuspenseQuery(
    questionResponseQueryOptions(
      accountId,
      workspaceId,
      questionnaireId,
      questionId,
      question.kind,
    ),
  )
  // The route already prefetches the questionnaire dashboard, whose tree
  // carries the current question's `assigned_to_me` (the single-question
  // detail endpoint doesn't).
  const { data: questionnaire } = useSuspenseQuery(
    questionnaireDashboardQueryOptions(accountId, workspaceId, questionnaireId),
  )
  const navigation = useQuestionNavigation({
    accountId,
    workspaceId,
    questionnaireId,
    currentQuestionId: questionId,
  })

  const captureOpened = useEffectEvent(() => {
    posthog.capture('question_opened', {
      account_id: accountId,
      workspace_id: workspaceId,
      questionnaire_id: questionnaireId,
      question_id: questionId,
      question_code: question.code,
      question_kind: question.kind,
      response_status: response.status,
    })
  })
  useEffect(() => {
    captureOpened()
  }, [questionId])

  const canEditAny = useCan('annwn:responses:edit:any')
  const canEditOwn = useCan('annwn:responses:edit:own')
  // An `:any` editor edits every question; an `:own` editor edits only questions
  // assigned to them. `assigned_to_me` is undefined for a question outside the
  // dashboard tree — treat that as not-assigned (fail closed; the BFF re-checks).
  // The question's place in the tree: its section titles the header, and its
  // summary carries `assigned_to_me`.
  const placed = flattenLeafQuestionSections(questionnaire.sections ?? [])
    .map((s) => ({
      section: s,
      summary: s.questions?.find((q) => q.uuid === questionId),
    }))
    .find((p) => p.summary)
  const section = placed?.section
  const assignedToMe = placed?.summary?.assigned_to_me ?? false
  // Accepting or discarding an AI draft is authoring, so it's gated on edit
  // (via `readOnly` below), NOT on `approve:*`: the BFF enforces `verify_can_edit`
  // on the version approve/reject endpoints. `approve:*` gates no endpoint.
  const canChangeStatus = useCan('annwn:responses:status')
  const canDraft = useCan('annwn:responses:draft')
  // Gates the drafter tuning knobs (tool effort, model tier) in the Draft
  // options menu. Internal-only; a caller who can draft but not configure sees
  // no tuning controls and drafts at the backend defaults.
  const canConfigureDrafter = useCan('annwn:responses:draft:configure')
  const canViewInternal = useCan('annwn:internal:read')
  const canReadDocuments = useCan('annwn:documents:read')
  const canAssign = useCan('annwn:responses:assign')
  // Comments: read gates the whole tab (a user may hold responses:read but not
  // comment:read); create gates the composer. Delete is server-driven per
  // comment via `can_delete`, so there's no slug to read here.
  const canCommentRead = useCan('annwn:responses:comment:read')
  const canCommentCreate = useCan('annwn:responses:comment:create')

  // Once an answer is flagged ready-for-review or signed off (locked) it can no
  // longer be edited and no new AI drafts may be started — the answer pane and
  // the AI Draft pane says why. Editing also requires an edit permission (an
  // :own editor additionally needs the question assigned to them; the BFF
  // re-enforces this on save).
  const readOnlyReason = getReadOnlyReason({
    status: response.status,
    canEditAny,
    canEditOwn,
    assignedToMe,
  })
  const readOnly = readOnlyReason !== null

  const aiUpdates = summariseAiUpdates(response, question)
  // Which context panes this user may open on this question. Shares the
  // drawer's cached guidance fetch (Worked examples only applies when the
  // question has some); narrow screens leave it to the drawer, which fetches
  // only once opened.
  const { data: guidance } = useQuery({
    ...guidanceQueryOptions(
      accountId,
      workspaceId,
      questionnaireId,
      questionId,
    ),
    enabled: wide,
  })
  const panelAccess = {
    canDraft,
    canCommentRead,
    hasPendingUpdates: aiUpdates.count > 0,
  }
  const shownLayout = visibleLayout(paneLayout, (p) =>
    isContextPaneShown(p, guidance, panelAccess),
  )
  const openPanes = openContextPanes(shownLayout)
  const paneControls = {
    layout: shownLayout,
    available: visibleContextPanes(guidance, panelAccess),
    onChange: setPaneLayout,
  }
  // Clicking a pending-update card (or paging the review list) focuses that
  // update and brings the AI Draft into view on it, where updates are reviewed.
  const revealUpdate = (versionId: string) => {
    setActiveUpdateId(versionId)
    if (wide) {
      setPaneLayout(revealPane(shownLayout, 'draft'))
    } else {
      setPanel('draft')
      setDrawerOpen(true)
    }
  }
  const draftInView = wide ? openPanes.includes('draft') : panel === 'draft'
  // When the AI Draft is in view but the user hasn't focused a specific update
  // yet, highlight the first pending update's field in the answer pane — the
  // review list defaults to that same first update, so this makes clear which
  // field it refers to. An explicit selection (a card/pill click) wins.
  const effectiveActiveUpdateId =
    activeUpdateId ??
    (draftInView ? (aiUpdates.updates[0]?.versionId ?? null) : null)
  // The bulk Accept/Reject-all banner is for matrix (multi-part) questions —
  // including a single-row static matrix, whose columns each carry a cell.
  // Only the accordion layout (multi-row static or dynamic) gets the
  // "across N rows" copy; the flat single-row case uses the plain "{{count}} AI
  // updates" copy (matrix={accordionMatrix}). Atomic questions have a single
  // field, so they keep their per-card marker and get no banner.
  const accordionMatrix = isAccordionMatrix(question)
  // A review-locked question — or one the user can't edit — shows no pending-AI
  // affordances (the answer reads plainly), so the bulk banner is suppressed
  // alongside the header badge. `!readOnly` already encodes the edit permission.
  const showBanner = aiUpdates.count > 0 && isMatrix(question) && !readOnly
  // A question that's in review shows the reviewer's approve / request-changes
  // banner instead — mutually exclusive with the AI-updates banner (a flagged
  // answer is read-only, so it carries no pending-AI affordances). Gated on the
  // same status permission as the pill; without it there's nothing to act on.
  const showReviewBanner =
    response.status === 'flagged_for_review' && canChangeStatus

  const approveAll = useApproveAllAiUpdates(
    accountId,
    workspaceId,
    questionnaireId,
    questionId,
  )
  const rejectAll = useRejectAllAiUpdates(
    accountId,
    workspaceId,
    questionnaireId,
    questionId,
  )
  const [rejectAllOpen, setRejectAllOpen] = useState(false)
  const banner = showBanner ? (
    <AiUpdatesBanner
      count={aiUpdates.count}
      affectedRows={aiUpdates.affectedRows}
      matrix={accordionMatrix}
      onAcceptAll={() => {
        approveAll.mutate(aiUpdates.updates)
      }}
      accepting={approveAll.isPending}
      acceptError={approveAll.isError}
      onRejectAll={() => setRejectAllOpen(true)}
      rejecting={rejectAll.isPending}
    />
  ) : showReviewBanner ? (
    <ResponseReviewBanner
      accountId={accountId}
      workspaceId={workspaceId}
      questionnaireId={questionnaireId}
      questionId={questionId}
    />
  ) : null

  // The answer pane's remount key. A scalar input snapshots its value with
  // `useState(initial)`, so it must remount to re-snapshot when the response's
  // version moves (save / accept / reject) — hence the version signature.
  //
  // A matrix (static OR dynamic) deliberately leaves its parent signature OUT:
  // its cells self-key for their own re-snapshot (see `ColumnStack`), so it
  // needs no parent-driven remount, while the accordion's expanded-row state is
  // UI state that must survive a parent refetch. A settling cell save propagates
  // the parent status (draft → accepted), moving the parent's version pointers —
  // keying on that would remount the whole matrix, collapsing the row the user
  // had opened and discarding a sibling cell's unsaved edit. A static matrix
  // keys its rows by stable predefined-subject uuids; a `dynamic_matrix` tracks
  // rows in local state and reconciles a bootstrapped row's server identity
  // (and AI-created rows) in place rather than via this remount (see
  // `DynamicMatrixInput`).
  //
  // The lock suffix is shared by every kind: it remounts the pane when flipping
  // to / from review so inputs that snapshot editability at mount (the long
  // rich-text editor) re-initialise.
  const lockSuffix = readOnly ? '-ro' : ''
  const answerPaneKey = isMatrix(question)
    ? `${question.uuid}${lockSuffix}`
    : `${question.uuid}-${versionSignature(response)}${lockSuffix}`

  // Everything a context panel needs, whether it shows as a drawer tab or a pane.
  const panelProps = {
    accountId,
    workspaceId,
    questionnaireId,
    questionId,
    questionKind: question.kind,
    optionCount: question.options?.length,
    rowLabels: matrixRowLabels(t, question, response),
    draftRunId,
    latestDraftRunId: response.latest_draft_run_id ?? null,
    aiUpdates: aiUpdates.updates,
    activeUpdateId,
    onActiveUpdateChange: revealUpdate,
    readOnlyReason,
    canDraft,
    canConfigureDrafter,
    canViewInternal,
    canCommentCreate,
    canReadDocuments,
  }

  return (
    <>
      <RejectReasonDialog
        open={rejectAllOpen}
        onOpenChange={setRejectAllOpen}
        count={aiUpdates.count}
        busy={rejectAll.isPending}
        error={rejectAll.isError}
        onSubmit={(reason) => {
          rejectAll.mutate(
            { updates: aiUpdates.updates, reason },
            { onSuccess: () => setRejectAllOpen(false) },
          )
        }}
      />
      <QuestionPageLayout
        wide={wide}
        drawerLabel={t('questionnaires.question.drawerLabel')}
        drawerOpen={drawerOpen}
        onDrawerOpenChange={setDrawerOpen}
        // Reset the drawer's scroll whenever its focused content changes: a new
        // question, a different drawer tab, or a different part/update of the
        // same question (clicking another sub-question re-focuses the Activity
        // panel without changing questionId).
        scrollResetKey={`${questionId}|${panel}|${activeUpdateId ?? ''}`}
        drawer={
          <Suspense fallback={null}>
            <QuestionDrawer
              {...panelProps}
              questionCode={question.code}
              panel={panel}
              onPanelChange={setPanel}
              onCollapse={() => {
                setDrawerOpen(false)
              }}
              canCommentRead={canCommentRead}
            />
          </Suspense>
        }
        panes={{
          ...paneControls,
          renderPane: (pane) => (
            <Suspense fallback={null}>
              <QuestionPanelBody panel={pane} {...panelProps} />
            </Suspense>
          ),
        }}
        header={
          <QuestionPageHeader
            question={question}
            sectionTitle={section?.title}
            aiUpdateCount={readOnly ? 0 : aiUpdates.count}
            banner={banner}
            navigation={navigation}
            pickerControl={
              <QuestionPicker
                accountId={accountId}
                workspaceId={workspaceId}
                questionnaireId={questionnaireId}
                currentQuestionId={questionId}
                variant="ghost"
              />
            }
            viewControl={
              wide ? (
                <EditViewMenu {...paneControls} />
              ) : (
                <DrawerToggle
                  open={drawerOpen}
                  onOpen={() => {
                    setDrawerOpen(true)
                  }}
                />
              )
            }
            statusControl={
              <ResponseStatusChip
                accountId={accountId}
                workspaceId={workspaceId}
                questionnaireId={questionnaireId}
                questionId={questionId}
                status={response.status}
                hasPendingAiUpdates={aiUpdates.count > 0}
                canChangeStatus={canChangeStatus}
              />
            }
            ownerControl={
              <DataOwnerChip
                accountId={accountId}
                workspaceId={workspaceId}
                questionnaireId={questionnaireId}
                questionId={questionId}
                canAssign={canAssign}
              />
            }
          />
        }
        body={
          <QuestionInput
            key={answerPaneKey}
            accountId={accountId}
            workspaceId={workspaceId}
            questionnaireId={questionnaireId}
            question={question}
            response={response}
            activeUpdateId={effectiveActiveUpdateId}
            onRevealUpdate={revealUpdate}
            readOnly={readOnly}
          />
        }
      />
    </>
  )
}
